/**
 * TranslationService class handles loading, parsing, and serving translated strings
 * from properties files. It supports multiple locales, UTF-8 encoding, and parameter
 * substitution in translated strings.
 * @author shabars
 */
class TranslationService {
    constructor() {
        this.messages = {};
        this.currentLocale = 'en';
    }

    /**
     * Maps and translates error messages to their localized versions.
     * @param {Error} error - The error object containing the message to translate
     * @returns {string} Translated error message or original message if no translation exists
     * @example
     * const error = new Error('Failed to fetch');
     * const translatedError = translationService.getErrorMessage(error);
     * // Returns the translated version of "Failed to fetch" in current locale
     */
    getErrorMessage(error) {
        const errorMap = {
            'Failed to fetch': 'genericFetchError',
        };

        const errorKey = errorMap[error.message];
        if (errorKey) {
            return this.translate(errorKey);
        }
        return error.message;
    }

    /**
     * Loads and parses translations from a properties file for the specified locale.
     * Falls back to English if loading fails.
     * @param {string} locale - The locale code (e.g., 'en', 'es', 'ja')
     * @returns {Promise<void>}
     * @throws {Error} When translation file cannot be loaded
     * @example
     * await translationService.loadTranslations('es');
     */
    async loadTranslations(locale) {
        try {
            const response = await fetch(`/translations/${locale}.properties`);
            // Explicitly set UTF-8 encoding
            const text = await response.text();
            const decoder = new TextDecoder('utf-8');
            const encoder = new TextEncoder();
            const decodedText = decoder.decode(encoder.encode(text));

            this.messages[locale] = this.parseProperties(decodedText);
            this.currentLocale = locale;
        } catch (error) {
            console.error(`Failed to load translations for ${locale}:`, error);
            if (locale !== 'en') {
                await this.loadTranslations('en');
            }
        }
    }

    /**
     * Parses properties file content into a key-value object.
     * Ignores comments (lines starting with #).
     * @private
     * @param {string} text - Raw content of the properties file
     * @returns {Object.<string, string>} Parsed translations
     * @example
     * // Input text:
     * // welcome=Hello
     * // goodbye=Goodbye
     * const messages = parseProperties(text);
     * // Returns: { welcome: "Hello", goodbye: "Goodbye" }
     */
    parseProperties(text) {
        const messages = {};
        const lines = text.split('\n');

        lines.forEach(line => {
            line = line.trim();
            if (line && !line.startsWith('#')) {
                const [key, ...valueParts] = line.split('=');
                const value = valueParts.join('=').trim();
                // Decode both Unicode escape sequences and UTF-8
                messages[key.trim()] = this.decodeUnicode(value);
            }
        });

        return messages;
    }

    /**
     * Decodes Unicode escape sequences in a string.
     * @private
     * @param {string} str - String containing Unicode escape sequences
     * @returns {string} Decoded string with actual Unicode characters
     * @example
     * const decoded = decodeUnicode('\\u0048\\u0065\\u006C\\u006C\\u006F');
     * // Returns: "Hello"
     */
    decodeUnicode(str) {
        // Handle both Unicode escape sequences and UTF-8 characters
        return str
            .replace(/\\u([0-9a-fA-F]{4})/g, (match, grp) =>
                String.fromCharCode(parseInt(grp, 16))
            )
            .replace(/\\u([0-9a-fA-F]{4})/g, (match, grp) =>
                String.fromCharCode(parseInt(grp, 16))
            );
    }

    /**
     * Translates a key to its corresponding message in the current locale.
     * Supports parameter substitution using {0}, {1}, etc.
     * @param {string} key - Translation key to look up
     * @param {...*} args - Values to substitute into the translated string
     * @returns {string} Translated and substituted string
     * @example
     * // Assuming translation: "hello=Hello, {0}!"
     * const greeting = translate('hello', 'John');
     * // Returns: "Hello, John!"
     */
    translate(key, ...args) {
        const message = this.messages[this.currentLocale]?.[key] || key;
        return args.reduce((str, arg, i) =>
                str.replace(`{${i}}`, arg),
            message
        );
    }

    /**
     * Sets the current locale and loads its translations.
     * @param {string} locale - Locale code to set
     * @returns {Promise<void>} Promise that resolves when translations are loaded
     * @example
     * await translationService.setLocale('fr');
     */
    setLocale(locale) {
        this.currentLocale = locale;
        return this.loadTranslations(locale);
    }
}

/**
 * Singleton instance of TranslationService.
 * @type {TranslationService}
 */
export const translationService = new TranslationService();